import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import { useTranslation } from 'react-i18next';
import useAppContext from '../../../context/useAppContext';
import { usePermissions } from '../../../user/permissions';
import { Button, Card, InfoText, Status, TextArea, TitleCard } from '@weezevent/nacre';
import Style from './billing.module.css';
import StyleDetail from './detail.module.css';

function InlineTimezoneDate({ date }) {
    if (!date) {
        return null;
    }
    const [{ event }] = useAppContext();

    const dateObject = new Date(date);
    const tzDate = moment(dateObject).tz(event.timezone, true);

    const formatter_date = new Intl.DateTimeFormat(event.default_language, { dateStyle: 'short' });
    const formatted_date = formatter_date.format(tzDate);
    const formatter_time = new Intl.DateTimeFormat(event.default_language, { timeStyle: 'short' });
    const formatted_time = formatter_time.format(tzDate);

    return (
        <span>
            <span className={StyleDetail['medium']}>{formatted_date}</span>
            <span>
                {' '}
                {formatted_time} {event.timezone}
            </span>
        </span>
    );
}

function PaymentPlanStatus({ status }) {
    const { t } = useTranslation();
    if (!status) {
        return null;
    }

    const mappingStatus = {
        P: {
            text: 'paymentinvoicing.payment_plan.status.pending',
            color: 'orange'
        },
        V: {
            text: 'paymentinvoicing.payment_plan.status.validated',
            color: 'green'
        },
        T: {
            text: 'paymentinvoicing.payment_plan.status.terminated',
            color: 'red'
        },
        A: {
            text: 'paymentinvoicing.payment_plan.status.aborted',
            color: 'red'
        }
    };

    return <Status color={mappingStatus[status].color} responsive text={t(mappingStatus[status].text)} />;
}

function PaymentPlanDetailData({ paymentPlan }) {
    const { t } = useTranslation();
    const currencyMapping = {
        EUR: '€',
        CAD: '$',
        CHF: 'CHF',
        USD: '$',
        GBP: '£'
    };

    if (!paymentPlan?.id) {
        return null;
    }

    const fieldsPaymentPlan = [
        {
            label: 'payment_method',
            value: 'Pay Over Time'
        },
        {
            label: 'payment_date',
            value: <InlineTimezoneDate date={paymentPlan.latest_successful_payment_date} />
        },
        {
            label: 'pay_over_time.type',
            value: `Pay Over Time x ${paymentPlan.total_instalments}`
        },
        {
            label: 'pay_over_time.status',
            value: <PaymentPlanStatus status={paymentPlan.status} />
        },
        // {
        //     label: 'pay_over_time.frequency',
        //     value: ''
        // },
        {
            label: 'pay_over_time.instalments',
            value: `${paymentPlan.instalments_paid} / ${paymentPlan.total_instalments}`
        },
        {
            label: 'pay_over_time.amount',
            value: `${Number.parseFloat((paymentPlan.total_amount - paymentPlan.remaining_amount) / 100).toFixed(2)} / ${Number.parseFloat(paymentPlan.total_amount / 100).toFixed(
                2
            )}${currencyMapping[paymentPlan.currency]}`
        },
        {
            label: 'pay_over_time.remaining_due_amount',
            value: Number.parseFloat(paymentPlan.remaining_amount / 100).toFixed(2) + currencyMapping[paymentPlan.currency]
        },
        ...(paymentPlan.instalments_history?.at(-1)
            ? [
                  {
                      label: 'pay_over_time.last_instalment',
                      value: (
                          <span>
                              {Number.parseFloat(paymentPlan.instalments_history?.at(-1)?.amount).toFixed(2) + currencyMapping[paymentPlan.instalments_history?.at(-1)?.currency]}
                              <InlineTimezoneDate date={paymentPlan.instalments_history?.at(-1)?.attempted_at} />
                          </span>
                      )
                  }
              ]
            : []),
        ...(paymentPlan.instalments_schedule?.at(0)
            ? [
                  {
                      label: 'pay_over_time.next_instalment',
                      value: (
                          <span>
                              {Number.parseFloat(paymentPlan.instalments_schedule?.at(0)?.amount).toFixed(2) + currencyMapping[paymentPlan.instalments_schedule?.at(0)?.currency]}
                              <InlineTimezoneDate date={paymentPlan.instalments_schedule?.at(0)?.due_date} />
                          </span>
                      )
                  }
              ]
            : [])
    ];

    return (
        <table className={Style['table-payment-plan']}>
            <tbody>
                {fieldsPaymentPlan.map((field, index) => (
                    <tr key={index}>
                        <td className={Style['td-value']}>
                            <span>
                                {`${field.label.includes('pay_over_time') ? t('paymentinvoicing.payment_plan.pay_over_time.prefix') : ''}${t(
                                    'paymentinvoicing.payment_plan.' + field.label
                                )}`}
                            </span>
                        </td>
                        <td className={Style['td-field']}>
                            <InfoText>{field.value}</InfoText>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function RawPaymentPlanData({ paymentPlan }) {
    const paymentPlanData = React.useMemo(() => {
        const paymentObject = paymentPlan?.object ? paymentPlan?.object : paymentPlan;
        if (!paymentObject) {
            return {};
        }
        delete paymentObject['__meta'];
        delete paymentObject['__params'];
        return paymentObject;
    }, [paymentPlan]);
    return <TextArea value={JSON.stringify(paymentPlanData, null, '\t')} disabled style={{ 'margin-top': '15px', height: '600px', width: '100%', resize: 'none' }} />;
}

export default function PaymentPlanDetail({ loading, paymentPlan }) {
    const { t } = useTranslation();
    const permissions = usePermissions();

    const [showAdminData, setShowAdminData] = React.useState(false);

    return (
        <Card loading={loading || !paymentPlan}>
            <Card.Header>
                <div className={Style['header']}>
                    <div className={Style['title-container']}>
                        <TitleCard title={t('paymentinvoicing.payment')} />
                    </div>
                </div>
            </Card.Header>
            <Card.Content>
                <PaymentPlanDetailData paymentPlan={paymentPlan} />
                {permissions.capacities.has('PyvarCapacity.ADMIN') && (
                    <Button label={t('paymentinvoicing.payment_data_cta')} primary disabled={showAdminData} onClick={() => setShowAdminData(true)} />
                )}
                {permissions.capacities.has('PyvarCapacity.ADMIN') && showAdminData && <RawPaymentPlanData paymentPlan={paymentPlan} />}
            </Card.Content>
        </Card>
    );
}

PaymentPlanDetail.propTypes = {
    paymentPlan: PropTypes.object,
    loading: PropTypes.bool
};
